import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Heading,
  Loader,
  ScrollView,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  View,
  Icon,
  Alert,
  SelectField,
  Accordion,
  CheckboxField,
  useAuthenticator,
  Grid
} from "@aws-amplify/ui-react";
import "./Clienti.css";
import { Modal, Form } from "react-bootstrap";
import { MdBlock, MdEdit, MdLock, MdLockOpen } from "react-icons/md";
import { blockUserAPI, getClientiListAPI, unblockUserAPI } from "../../data/api";
import { getUserRoleLabel } from "../../data/interfaces";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { BsSortDown, BsSortUp } from "react-icons/bs";
import { clientiAttributes, clientiColumns, getLabel } from "./config";
import ClientiTable from "./ClientiTable";
import ClienteFilters from "./ClienteFilters";
import { useMounted } from "../../utils/utils";

const Clienti = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const mounted = useMounted();
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [emptyState, setEmptyState] = useState(false);
  const [isLastPage, setIsLastPage] = useState(false);
  const pageSize = 10;

  const [order, setOrder] = useState(undefined)
  const [sortKey, setSortKey] = useState(undefined)
  const updateOrder = (key) => {
    if(key === sortKey) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setOrder('asc');
    }
    setClienti([]);
  }  

  useEffect(() => {
    mounted && getClientiList();
  }, [sortKey, order]);

  const [selectedColumns, setSelectedColumns] =  useState(() => {
    const storage = localStorage.getItem('ricercaClientiColumns-'+user?.username);
    return storage ? JSON.parse(storage) : clientiColumns;
  });
  const checkColumn = (id) => {
    if(!selectedColumns.includes(id)) {
      setSelectedColumns([...selectedColumns, id]);
      localStorage.setItem('ricercaClientiColumns-'+user?.username, JSON.stringify([...selectedColumns, id]));
    } else {
      setSelectedColumns(selectedColumns.filter(t => t != id))
      localStorage.setItem('ricercaClientiColumns-'+user?.username, JSON.stringify(selectedColumns.filter(t => t != id)));
    }
  }

  const getClientiList = async () => {
    setLoading(true)
    setEmptyState(false);
    setError(false);
    try {
      const clientiList = await getClientiListAPI(clienti.length, pageSize, sortKey, order, filters);
      setIsLastPage(clientiList.last);
      setClienti([...clienti, ...clientiList.clienti])
      setLoading(false);
    } catch (e) {
      if(e.statusCode === 404){
        if(clienti.length === 0){
          console.log('No clienti found', e);
          setEmptyState(true);
        } else {
          setIsLastPage(true);
        }
      } else {
        console.log('Could not load clienti list: ', e);
        setError(true);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getClientiList();
  }, []);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [filters, setFilters] = useState(() => {
    const storage = localStorage.getItem('filterRicercaCliente'+user?.username);
    const filters = storage ? JSON.parse(storage) : {};
    setActiveFilters(Object.keys(filters).filter(f => filters[f].length != 0 && filters[f] !== '').length ?? 0)
    return filters;
  });;
  const setFiltersRicerca = (e) => {
    let newValue;
    if(e.target.value === "" || JSON.stringify(e.target.value) === '{}' || JSON.stringify(e.target.value) === '[]'){
      newValue = {
        ...filters
      }
      delete newValue[e.target.name];
    } else {
      newValue = {
        ...filters,
        [e.target.name]: e.target.value,
      };
    }
    setFilters(newValue);
    localStorage.setItem('filterRicercaCliente'+user?.username, JSON.stringify(newValue));
  };
  // State to manage reload key whenever the filters are applied
  const [reloadKey, setReloadKey] = useState(0);

  const handleFilterChange = (e) => {
    setFiltersRicerca(e);
  };

  const resetFilters = (e) => {
    setClienti([]);
    setFilters({});
    localStorage.removeItem('filterRicercaCliente'+user?.username);
    setShowFilterModal(false);
    setActiveFilters(0);
    setReloadKey(reloadKey+1)
  };

  const handleFilterSubmit = () => {
    setClienti([]);
    setShowFilterModal(false);
    setActiveFilters(Object.keys(filters).filter(f => filters[f].length != 0 && filters[f] !== '').length ?? 0)
    setReloadKey(reloadKey+1)
  };

  useEffect(() => {
    mounted && getClientiList();
  }, [reloadKey]);

  const loadNextPage = async () => {
    if(!isLastPage){
      getClientiList();
    }
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadNextPage();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadNextPage]);

  return (
    <View maxWidth="100%" padding="0rem" minHeight="100vh">
      <View
        backgroundColor="var(--amplify-colors-white)"
        borderRadius="6px"
        maxWidth="100%"
        padding="1rem"
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2>Anagrafica clienti</h2>
          <View>
            {activeFilters > 0 && <Button variation="warning" onClick={resetFilters} marginLeft = "10px">
              Rimuovi tutti i filtri
            </Button>}
            <Button 
              onClick={() => setShowFilterModal(true)} 
              variation={activeFilters > 0 ? 'primary' : undefined} marginLeft = "10px">Filtri di ricerca ({activeFilters})
            </Button>
            <Button className="creaNuovoCliente" onClick={() => navigate('/clienti/new')} marginLeft = "10px">Crea nuovo cliente</Button>
          </View>
        </div>
        <Accordion.Container marginBottom="20px">
          <Accordion.Item value="Accordion-item">
            <Accordion.Trigger>
              <i>Seleziona colonne...</i>
              <Accordion.Icon />
            </Accordion.Trigger>
            <Accordion.Content>
              <Grid 
                  templateColumns={{ base: '1fr', large: '1fr 1fr 1fr 1fr' }}
                  gap='5px'
                  >          
                {clientiAttributes.map(c => (
                  <CheckboxField
                    key={c.id}
                    value={c.id}
                    name={c.id}
                    label={c.value}
                    checked={selectedColumns.includes(c.id)}
                    onChange={() => checkColumn(c.id)}
                    isDisabled={(!selectedColumns.includes(c.id) && selectedColumns.length > 7) || (selectedColumns.includes(c.id) && selectedColumns.length === 1)}
                  ></CheckboxField>
                ))}
              </Grid>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Container>
        <ScrollView width="100%">
          {
            error ? <Alert variation="error">Errore nel caricamento dei dati</Alert> :
            emptyState ? <Alert variation="info">Nessun cliente trovato</Alert> :
              <>
                <ClientiTable
                  columns={selectedColumns}
                  clienti = {clienti}
                  order={order}
                  sortKey={sortKey}
                  onUpdateOrder={(c) => updateOrder(c)}
                  onClickCliente={(id) => navigate('/clienti/detail?id='+id)}
                ></ClientiTable>
                <div ref={lastItemRef} />
              </>
          }
          { loading && <Loader/> } 
        </ScrollView>
      </View>

      <Modal show={showFilterModal} onHide={() => {setShowFilterModal(false); handleFilterSubmit();}} size="xl">
        <Modal.Header>
          <Modal.Title>Imposta filtri di ricerca</Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            <Button variation="primary" onClick={handleFilterSubmit} style={{ marginRight: '10px' }}>
              Applica filtri
            </Button>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowFilterModal(false); }}></button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <ScrollView margin="20px">
            <ClienteFilters 
              value={filters}
              savedValue={filters}
              onChange={handleFilterChange}>
            </ClienteFilters>
          </ScrollView>
        </Modal.Body>
        <Modal.Footer>
          <Button variation="primary" onClick={handleFilterSubmit} marginLeft = "10px">
            Applica filtri
          </Button>
        </Modal.Footer>
      </Modal>
    </View>
  );
};

export default Clienti;
